/**
 * Schema validator for Power Automate flow definitions
 */

import type {
  FlowDefinition,
  TriggerDefinition,
  ActionDefinition,
} from "../api/types.js";

/**
 * Validation error details
 */
export interface ValidationError {
  path: string;
  message: string;
  severity: "error" | "warning";
}

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

/**
 * Create an empty validation result
 */
function emptyResult(): ValidationResult {
  return { valid: true, errors: [], warnings: [] };
}

/**
 * Add an error to the result
 */
function addError(result: ValidationResult, path: string, message: string): void {
  result.errors.push({ path, message, severity: "error" });
  result.valid = false;
}

/**
 * Add a warning to the result
 */
function addWarning(result: ValidationResult, path: string, message: string): void {
  result.warnings.push({ path, message, severity: "warning" });
}

/**
 * Validate a flow definition
 */
export function validateFlowDefinition(definition: FlowDefinition): ValidationResult {
  const result = emptyResult();

  // Check schema
  if (!definition.$schema) {
    addError(result, "$schema", "Missing $schema property");
  } else if (!definition.$schema.includes("workflowdefinition.json")) {
    addWarning(result, "$schema", `Unexpected schema: ${definition.$schema}`);
  }

  // Check content version
  if (!definition.contentVersion) {
    addError(result, "contentVersion", "Missing contentVersion property");
  }

  // Check triggers
  if (!definition.triggers || Object.keys(definition.triggers).length === 0) {
    addError(result, "triggers", "Flow must have at least one trigger");
  } else {
    for (const [name, trigger] of Object.entries(definition.triggers)) {
      validateTrigger(trigger, `triggers.${name}`, result);
    }
  }

  // Check actions
  if (!definition.actions || Object.keys(definition.actions).length === 0) {
    addWarning(result, "actions", "Flow has no actions");
  } else {
    validateActions(definition.actions, result);
  }

  return result;
}

/**
 * Validate a trigger definition
 */
function validateTrigger(
  trigger: TriggerDefinition,
  path: string,
  result: ValidationResult
): void {
  if (!trigger.type) {
    addError(result, `${path}.type`, "Trigger must have a type");
    return;
  }

  // Validate based on trigger type
  switch (trigger.type) {
    case "Request":
      validateRequestTrigger(trigger, path, result);
      break;
    case "Recurrence":
      validateRecurrenceTrigger(trigger, path, result);
      break;
    case "OpenApiConnection":
      validateConnectorTrigger(trigger, path, result);
      break;
    default:
      // Unknown trigger type - just check for basic structure
      break;
  }
}

/**
 * Validate a Request trigger
 */
function validateRequestTrigger(
  trigger: TriggerDefinition,
  path: string,
  result: ValidationResult
): void {
  if (trigger.kind === "Http" && trigger.inputs) {
    const inputs = trigger.inputs;
    if (inputs.schema && typeof inputs.schema !== "object") {
      addError(result, `${path}.inputs.schema`, "Schema must be an object");
    }
  }
}

/**
 * Validate a Recurrence trigger
 */
function validateRecurrenceTrigger(
  trigger: TriggerDefinition,
  path: string,
  result: ValidationResult
): void {
  if (!trigger.recurrence) {
    addError(result, `${path}.recurrence`, "Recurrence trigger must have recurrence settings");
    return;
  }

  const { frequency, interval } = trigger.recurrence;

  if (!frequency) {
    addError(result, `${path}.recurrence.frequency`, "Recurrence frequency is required");
  }

  if (interval === undefined || interval < 1) {
    addError(result, `${path}.recurrence.interval`, "Recurrence interval must be at least 1");
  }

  // Validate frequency-specific constraints
  const validFrequencies = ["Second", "Minute", "Hour", "Day", "Week", "Month"];
  if (frequency && !validFrequencies.includes(frequency)) {
    addError(
      result,
      `${path}.recurrence.frequency`,
      `Invalid frequency: ${frequency}. Must be one of: ${validFrequencies.join(", ")}`
    );
  }
}

/**
 * Validate a connector trigger
 */
function validateConnectorTrigger(
  trigger: TriggerDefinition,
  path: string,
  result: ValidationResult
): void {
  if (!trigger.inputs) {
    addError(result, `${path}.inputs`, "Connector trigger must have inputs");
    return;
  }

  const inputs = trigger.inputs;

  if (!inputs.host) {
    addError(result, `${path}.inputs.host`, "Connector trigger must have host configuration");
    return;
  }

  const host = inputs.host as Record<string, unknown>;

  if (!host.apiId) {
    addError(result, `${path}.inputs.host.apiId`, "Missing connector API ID");
  }

  if (!host.connectionName) {
    addError(result, `${path}.inputs.host.connectionName`, "Missing connection reference");
  }

  if (!host.operationId) {
    addError(result, `${path}.inputs.host.operationId`, "Missing operation ID");
  }
}

/**
 * Validate all actions
 */
function validateActions(
  actions: Record<string, ActionDefinition>,
  result: ValidationResult
): void {
  const actionNames = new Set(Object.keys(actions));

  for (const [name, action] of Object.entries(actions)) {
    validateAction(action, `actions.${name}`, actionNames, result);
  }

  // Check for circular dependencies at top level
  detectCircularDependencies(actions, actionNames, "actions", result);
}

/**
 * Validate a single action
 */
function validateAction(
  action: ActionDefinition,
  path: string,
  allActions: Set<string>,
  result: ValidationResult
): void {
  if (!action.type) {
    addError(result, `${path}.type`, "Action must have a type");
    return;
  }

  // Validate runAfter references
  if (action.runAfter) {
    for (const depName of Object.keys(action.runAfter)) {
      if (!allActions.has(depName)) {
        addError(
          result,
          `${path}.runAfter.${depName}`,
          `References non-existent action: ${depName}`
        );
      }

      const statuses = action.runAfter[depName];
      const validStatuses = ["Succeeded", "Failed", "Skipped", "TimedOut"];
      if (!statuses) continue;
      for (const status of statuses) {
        if (!validStatuses.includes(status)) {
          addError(
            result,
            `${path}.runAfter.${depName}`,
            `Invalid status: ${status}. Must be one of: ${validStatuses.join(", ")}`
          );
        }
      }
    }
  }

  // Validate based on action type
  switch (action.type) {
    case "OpenApiConnection":
      validateConnectorAction(action, path, result);
      break;
    case "If":
      validateConditionAction(action, path, allActions, result);
      break;
    case "Foreach":
      validateForeachAction(action, path, allActions, result);
      break;
    case "Switch":
      validateSwitchAction(action, path, allActions, result);
      break;
    case "Scope":
      validateScopeAction(action, path, allActions, result);
      break;
    case "Until":
      validateUntilAction(action, path, allActions, result);
      break;
    default:
      // Basic validation for other action types
      break;
  }
}

/**
 * Validate a connector action
 */
function validateConnectorAction(
  action: ActionDefinition,
  path: string,
  result: ValidationResult
): void {
  if (!action.inputs) {
    addError(result, `${path}.inputs`, "Connector action must have inputs");
    return;
  }

  const inputs = action.inputs;

  if (!inputs.host) {
    addError(result, `${path}.inputs.host`, "Connector action must have host configuration");
    return;
  }

  const host = inputs.host as Record<string, unknown>;

  if (!host.apiId) {
    addError(result, `${path}.inputs.host.apiId`, "Missing connector API ID");
  }

  if (!host.connectionName) {
    addError(result, `${path}.inputs.host.connectionName`, "Missing connection reference");
  }

  if (!host.operationId) {
    addError(result, `${path}.inputs.host.operationId`, "Missing operation ID");
  }
}

/**
 * Validate nested actions using a local scope for runAfter references.
 * Nested actions (inside If, Foreach, Switch, Scope, Until) should only
 * reference siblings within the same scope, not top-level actions.
 * Also performs circular dependency detection within the nested scope.
 */
function validateNestedActions(
  actions: Record<string, ActionDefinition>,
  path: string,
  result: ValidationResult
): void {
  const localScope = new Set(Object.keys(actions));
  for (const [name, subAction] of Object.entries(actions)) {
    validateAction(subAction, `${path}.${name}`, localScope, result);
  }

  // Detect circular dependencies within this nested scope
  detectCircularDependencies(actions, localScope, path, result);
}

/**
 * Detect circular dependencies among a set of actions
 */
function detectCircularDependencies(
  actions: Record<string, ActionDefinition>,
  actionNames: Set<string>,
  path: string,
  result: ValidationResult
): void {
  const visited = new Set<string>();
  const visiting = new Set<string>();

  const detectCycle = (actionName: string, stack: string[] = []): string[] | null => {
    if (visiting.has(actionName)) {
      return [...stack, actionName];
    }
    if (visited.has(actionName)) {
      return null;
    }

    visiting.add(actionName);
    stack.push(actionName);

    const action = actions[actionName];
    if (action?.runAfter) {
      for (const depName of Object.keys(action.runAfter)) {
        if (actionNames.has(depName)) {
          const cycle = detectCycle(depName, stack);
          if (cycle) return cycle;
        }
      }
    }

    visiting.delete(actionName);
    visited.add(actionName);
    return null;
  };

  for (const actionName of actionNames) {
    const cycle = detectCycle(actionName);
    if (cycle) {
      addError(result, path, `Circular dependency detected: ${cycle.join(" -> ")}`);
      break;
    }
  }
}

/**
 * Validate a condition (If) action
 */
function validateConditionAction(
  action: ActionDefinition,
  path: string,
  _allActions: Set<string>,
  result: ValidationResult
): void {
  if (!action.expression) {
    addError(result, `${path}.expression`, "Condition action must have an expression");
  }

  if (!action.actions || Object.keys(action.actions).length === 0) {
    addWarning(result, `${path}.actions`, "Condition 'if true' branch has no actions");
  } else {
    validateNestedActions(action.actions, `${path}.actions`, result);
  }

  if (action.else?.actions) {
    validateNestedActions(action.else.actions, `${path}.else.actions`, result);
  }
}

/**
 * Validate a Foreach action
 */
function validateForeachAction(
  action: ActionDefinition,
  path: string,
  _allActions: Set<string>,
  result: ValidationResult
): void {
  if (!action.foreach) {
    addError(result, `${path}.foreach`, "Foreach action must have a foreach expression");
  }

  if (!action.actions || Object.keys(action.actions).length === 0) {
    addWarning(result, `${path}.actions`, "Foreach loop has no actions");
  } else {
    validateNestedActions(action.actions, `${path}.actions`, result);
  }
}

/**
 * Validate a Switch action
 */
function validateSwitchAction(
  action: ActionDefinition,
  path: string,
  _allActions: Set<string>,
  result: ValidationResult
): void {
  if (!action.expression) {
    addError(result, `${path}.expression`, "Switch action must have an expression");
  }

  if (!action.cases || Object.keys(action.cases).length === 0) {
    addWarning(result, `${path}.cases`, "Switch has no cases");
  } else {
    for (const [caseName, caseData] of Object.entries(action.cases)) {
      if (caseData.actions) {
        validateNestedActions(caseData.actions, `${path}.cases.${caseName}.actions`, result);
      }
    }
  }

  if (action.default?.actions) {
    validateNestedActions(action.default.actions, `${path}.default.actions`, result);
  }
}

/**
 * Validate a Scope action
 */
function validateScopeAction(
  action: ActionDefinition,
  path: string,
  _allActions: Set<string>,
  result: ValidationResult
): void {
  if (!action.actions || Object.keys(action.actions).length === 0) {
    addWarning(result, `${path}.actions`, "Scope has no actions");
  } else {
    validateNestedActions(action.actions, `${path}.actions`, result);
  }
}

/**
 * Validate an Until action
 */
function validateUntilAction(
  action: ActionDefinition,
  path: string,
  _allActions: Set<string>,
  result: ValidationResult
): void {
  if (!action.expression) {
    addError(result, `${path}.expression`, "Until action must have an expression");
  }

  if (!action.actions || Object.keys(action.actions).length === 0) {
    addWarning(result, `${path}.actions`, "Until loop has no actions");
  } else {
    validateNestedActions(action.actions, `${path}.actions`, result);
  }
}

/**
 * Quick validation check - returns true if valid
 */
export function isValidFlowDefinition(definition: FlowDefinition): boolean {
  return validateFlowDefinition(definition).valid;
}

/**
 * Get validation errors as formatted strings
 */
export function formatValidationErrors(result: ValidationResult): string[] {
  const messages: string[] = [];

  for (const error of result.errors) {
    messages.push(`ERROR [${error.path}]: ${error.message}`);
  }

  for (const warning of result.warnings) {
    messages.push(`WARNING [${warning.path}]: ${warning.message}`);
  }

  return messages;
}
